import { useEffect, useState } from 'react'
import {
  Download, Vault, TrendingUp, TrendingDown, Wallet, Info, AlertTriangle, Radio,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, RefreshButton, Stat, Tag } from '../ui'
import { CoinIcon } from '../../components/Market'
import { get } from '../../lib/api'
import { fa, num, compact, asArray, clsx } from '../../lib/format'

const signed = (v: number) => (v > 0 ? 'text-up' : v < 0 ? 'text-down' : 'text-slate-400')
const sign = (v: number) => (v > 0 ? '+' : '')

export default function TreasuryStatus() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [onlyHeld, setOnlyHeld] = useState(true)

  const load = () => {
    setLoading(true)
    get('/admin/accounting/treasury-status')
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const all = asArray<any>(data?.assets)
  const assets = onlyHeld ? all.filter((a: any) => a.balance > 0) : all
  const t = data?.totals || {}
  const irt = data?.irt

  return (
    <>
      <PageHead
        title="وضعیت خزانه ارزینو"
        subtitle="موجودی، بهای تمام‌شده و ارزش روز همه دارایی‌ها"
        action={
          <div className="flex gap-2">
            <a href="/api/admin/accounting/treasury-status/export" className="btn-ghost !py-2 text-xs">
              <Download className="h-3.5 w-3.5" /> خروجی اکسل
            </a>
            <RefreshButton onClick={load} loading={loading} />
          </div>
        }
      />

      {/* قیمت‌ها زنده نیستند ⇒ «ارزش روز» و سود/زیان قابل استناد نیست */}
      {data && !data.pricesLive && (
        <div className="card mb-4 flex items-start gap-3 border-down/30 bg-down/[.07] p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-down" />
          <p className="text-xs leading-6 text-slate-200">
            قیمت‌ها از منبع معتبر به‌روز نمی‌شوند. «ارزش روز» و «سود/زیان تحقق‌نیافته»
            تا وصل‌شدن دوباره قیمت‌ها قابل استناد نیستند. «موجودی» و «ارزش دفتری»
            دقیق‌اند، چون به قیمت لحظه‌ای وابسته نیستند.
          </p>
        </div>
      )}
      {t.priceMissing > 0 && (
        <div className="card mb-4 flex items-start gap-3 border-gold/30 bg-gold/[.06] p-4">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-6 text-slate-200">
            برای {fa(t.priceMissing)} دارایی که موجودی دارد، نرخ بازار در دسترس نیست؛
            ارزش روزشان صفر حساب شده است.
          </p>
        </div>
      )}

      {/* ── جمع کل */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="ارزش دفتری کل" color="#7C5CFF" icon={<Vault className="h-5 w-5" />}
          value={num(t.bookValue || 0)} hint="مجموع آنچه پرداخته‌ایم" />
        <Stat label="ارزش روز کل" color="#22D3EE" icon={<Wallet className="h-5 w-5" />}
          value={num(t.marketValue || 0)} hint="به نرخ لحظه‌ای بازار" />
        <Stat label="سود/زیان تحقق‌نیافته"
          color={(t.unrealized || 0) >= 0 ? '#00E39A' : '#FF4D6D'}
          icon={(t.unrealized || 0) >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
          value={`${sign(t.unrealized || 0)}${num(t.unrealized || 0)}`}
          hint="تا نفروشیم، روی کاغذ است" />
        <Stat label="موجودی ریالی" color="#FFB020" icon={<Radio className="h-5 w-5" />}
          value={num(t.irtBalance || 0)}
          hint={`${fa(t.withBalance || 0)} دارایی دارای موجودی از ${fa(t.total || 0)}`} />
      </div>

      <Panel dense
        title="دارایی‌ها"
        action={
          <button onClick={() => setOnlyHeld((v) => !v)} className="btn-ghost !py-2 text-xs">
            {onlyHeld ? 'نمایش همه ارزها' : 'فقط دارای موجودی'}
          </button>
        }>
        <DataTable
          rows={assets} loading={loading} minWidth={1120}
          empty={onlyHeld ? 'هیچ دارایی با موجودی مثبت نیست' : 'ارزی تعریف نشده است'}
          columns={[
            {
              key: 'c', header: 'دارایی',
              cell: (r: any) => (
                <div className="flex items-center gap-2.5">
                  <CoinIcon symbol={r.currency} color={r.color} size={30} />
                  <div className="min-w-0">
                    <div className="truncate font-semibold text-slate-100">{r.faName}</div>
                    <div className="num text-[11px] text-slate-500" dir="ltr">{r.currency}</div>
                  </div>
                </div>
              ),
            },
            {
              key: 'b', header: 'موجودی',
              cell: (r: any) => (
                <span className={clsx('num', r.balance < 0 ? 'text-down' : 'text-slate-100')}>
                  {num(r.balance, 6)}
                </span>
              ),
            },
            {
              key: 'cb', header: 'میانگین بهای تمام‌شده',
              cell: (r: any) => (
                <span className="num text-slate-200">
                  {r.costBasis ? `${num(r.costBasis)} ت` : '—'}
                </span>
              ),
            },
            {
              key: 'bv', header: 'ارزش دفتری',
              cell: (r: any) => <span className="num text-slate-300">{r.bookValue ? num(r.bookValue) : '—'}</span>,
            },
            {
              key: 'mp', header: 'قیمت فعلی بازار',
              cell: (r: any) => (
                r.priceMissing
                  ? <Tag kind="gold">بدون نرخ</Tag>
                  : <span className="num text-slate-200">{num(r.marketPrice)} ت</span>
              ),
            },
            {
              key: 'mv', header: 'ارزش روز',
              cell: (r: any) => <span className="num font-semibold text-white">{r.marketValue ? num(r.marketValue) : '—'}</span>,
            },
            {
              key: 'u', header: 'سود/زیان تحقق‌نیافته',
              cell: (r: any) => (
                r.balance > 0 && r.costBasis ? (
                  <div className="leading-5">
                    <div className={clsx('num font-bold', signed(r.unrealized))}>
                      {sign(r.unrealized)}{num(r.unrealized)}
                    </div>
                    <div className={clsx('num text-[11px]', signed(r.unrealized))}>
                      {sign(r.unrealizedPercent)}{fa(r.unrealizedPercent)}٪
                    </div>
                  </div>
                ) : <span className="text-slate-600">—</span>
              ),
            },
            {
              key: 'g', header: 'بدهی به کاربران',
              cell: (r: any) => (
                <div className="leading-5">
                  <div className="num text-slate-300">{num(r.liability, 6)}</div>
                  {Math.abs(r.gap) > 1e-8 && (
                    <div className={clsx('num text-[11px]', r.gap < 0 ? 'text-down' : 'text-slate-500')}>
                      اختلاف {sign(r.gap)}{num(r.gap, 6)}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Panel>

      {irt && (
        <Panel dense className="mt-4" title="موجودی ریالی">
          <div className="flex flex-wrap items-center gap-6 p-4">
            <div>
              <div className="text-[11px] text-slate-500">موجودی</div>
              <div className="num mt-1 text-xl font-black text-white">{num(irt.balance)} تومان</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500">بدهی ریالی به کاربران</div>
              <div className="num mt-1 text-lg font-bold text-slate-300">{num(irt.liability)} تومان</div>
            </div>
            <p className="mr-auto max-w-md text-[11px] leading-6 text-slate-500">
              ریال واحد سنجش خودِ گزارش است، پس بهای تمام‌شده و سود/زیان برایش
              معنا ندارد و در جمع بالا هم نمی‌آید.
            </p>
          </div>
        </Panel>
      )}

      <div className="mt-3 space-y-1.5 px-1 text-[11px] leading-6 text-slate-500">
        <p>
          <b className="text-slate-400">ارزش دفتری</b> = موجودی × میانگین بهای تمام‌شده —
          یعنی چقدر پول بابت این دارایی داده‌ایم.
          <b className="mr-2 text-slate-400">ارزش روز</b> = موجودی × نرخ لحظه‌ای بازار.
          اختلافشان <b className="text-slate-400">سود/زیان تحقق‌نیافته</b> است: تا وقتی
          نفروخته‌ایم فقط روی کاغذ است و با نوسان بازار بالا و پایین می‌رود.
        </p>
        <p>
          <b className="text-slate-400">بدهی به کاربران</b> مجموع موجودی کیف‌پول‌های
          کاربران است. اختلاف چشمگیر آن با موجودی دفتری یعنی جایی یک ثبت جا افتاده و
          باید در دفتر کل دنبالش گشت.
        </p>
      </div>
    </>
  )
}
